import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Container, Heading, Grid, useColorMode } from 'theme-ui';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { getDashboardOverviewData, setDashboardFlag } from '../../../store/actions/dashboardAction';
import DashboardFilters from './Native/Filter';
import ProjectOverview from './Native/ProjectOverview/ProjectOverview';
import DoneProjectBreakdown from './Native/DoneProjectBreakdown/doneProjectBreakdown';
import { DepartmentItem, FilterValue, ManagersData } from '../../../types/dashboard.types';
import ResidentialAndCommercial from './Native/ResidentialVsCommercial/ResidentialVsCommercial';
import ProjectProgressChart from './Native/ProjectProgression/ProjectProgression';
import TradesDistribution from './Native/DoneProjectBreakdown/TradesDistribution';
import { useQueryParams } from './Helper/customHook';
import ClearFilterButton from './Components/ClearFilterButton';

const DashboardComponent: React.FC = () => {
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ]

  const [isPopoverOpen, setIsPopoverOpen] = useState("");
  const [progressionFilterPopover, setProgressionFilterPopover] = useState("")
  const popoverRef = useRef<HTMLDivElement>(null);
  const progressionFilterRef = useRef<HTMLDivElement>(null);

  // ---------------- Filters ----------------
  const [filterValue, setFilterValue] = useState<FilterValue>({
    time: "YTD",
    projectType: "All Projects",
    trades: "",
    year: [],
    month: [],
    projectManager: [],
    projectWorker: [],
    progressionTime: "YTD",
    progressionMonths: [],
    progressionYears: []
  });

  const dispatch = useDispatch();
  const [colorMode] = useColorMode();
  const bgDashboard = colorMode === 'light' ? "#ffffff" : "#2e354db8";
  const titleHeader = colorMode === 'light' ? "#453521" : "#dadada";
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
  const [showMonthsPicker, setShowMonthsPicker] = useState(false)
  const [showProjectManagerList, setShowProjectManagerList] = useState(false);
  const { dashboardData, allDataLoading, progressionLoading, flag, graphData, years } = useSelector((state: any) => state.dashboardData);
  const { createprojectMasterData } = useSelector((state: any) => state.project);
  const { allProjectManagers, allTaskWorkers, allTaskManager } = createprojectMasterData;
  const [managersData, setManagersData] = useState<ManagersData[]>([]);
  const { setParam, createUrl, searchParams, setArrayParam, removeParam, removeAllParams, hydrateFiltersFromUrl, setMultipleParams } = useQueryParams();
  const searchParamString = searchParams.toString();
  // -------------------------------------------------------------
  // Fetch Dashboard Data
  // -------------------------------------------------------------

  const fetchDashboardData = async () => {
    let url = createUrl(flag);
    // setFilterValue(filterValue)
    try {
      await dispatch(getDashboardOverviewData(url));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    onReloadHandler();
  }, [searchParamString]);

  const onReloadHandler = () => {
    let filterValue = hydrateFiltersFromUrl();
    calculateManagersList(filterValue.trades);
    setFilterValue(filterValue)
    if (filterValue.time === "Custom Selection") {
      setShowMonthYearPicker(true)
    } else if (filterValue.progressionTime === "Custom Selection") {
      setShowMonthsPicker(true)
    }
  }

  useEffect(() => {
    onReloadHandler();
  }, [])

  const uniqueById = (arr: any) => {
    const map = new Map();
    arr.forEach((item: any) => map.set(item._id, item));
    return Array.from(map.values());
  };

  const calculateManagersList = (trades: string) => {

    if (!trades) {
      return { show: false, data: [] };
    }

    if (trades === "All") {
      return {
        show: true,
        data: [...allProjectManagers],
      };
    }

    const tradeGroups = ["MECHANICAL", "ELECTRICAL", "PLUMBING", "STRUCTURAL"];

    if (tradeGroups.includes(trades)) {
      const allWorkerAndManager = [
        ...allTaskWorkers,
        ...allTaskManager,
      ];

      const filtered = allWorkerAndManager.filter((user: any) =>
        user.departments.includes(filterValue.departmentId)
      );

      return {
        show: true,
        data: uniqueById(filtered),
      };
    }

    return {
      show: false,
      data: [],
    };
  }

  useEffect(() => {
    setTimeout(() => {
      let { show, data } = calculateManagersList(filterValue.trades);
      setShowProjectManagerList(show);
      setManagersData(data);
    }, 1);
  }, [filterValue.trades,
    allProjectManagers,
    allTaskWorkers,
    allTaskManager,]);

  // -------------------------------------------------------------
  // Popover handling
  // -------------------------------------------------------------
  const togglePopover = useCallback((value: string) => {
    setIsPopoverOpen("");
    setProgressionFilterPopover("");
    setTimeout(() => {
      setIsPopoverOpen(value);
      setProgressionFilterPopover(value)
    }, 1);
  }, []);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
      setIsPopoverOpen("");
    }
    if (progressionFilterRef.current && !progressionFilterRef.current.contains(event.target as Node)) {
      setProgressionFilterPopover("");
    }
  }, [popoverRef, progressionFilterRef]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  // -------------------------------------------------------------
  // Filter option selection
  // -------------------------------------------------------------

  const isDepartmentItem = (value: any): value is DepartmentItem => { return (typeof value === "object" && value !== null && "name" in value && "_id" in value); };

  const handleOptionClick = useCallback(
    (value: string | DepartmentItem, name: string, flagValue: string = "dashboard-data") => {
      if (!value || !name) return;

      const isCustomTime = (name === "time" && value === "Custom Selection");
      const isProgressionMonths = (name === "progressionTime" && value === "Custom Selection")
      const isProgressionYears = (name === "progressionTime" && value === "Custom Selection")
      const isTradesChange = name === "trades";

      // UI state (safe)
      setShowMonthYearPicker(isCustomTime);
      setShowMonthsPicker(isProgressionMonths);

      // Filter state (pure)
      setFilterValue(prev => {
        const updated = { ...prev };

        if (isDepartmentItem(value)) {
          updated[name] = value.name;
          updated.departmentId = value.name === "All" ? undefined : value._id;
        } else {
          updated[name] = value;
        }

        if (name === "time" && !isCustomTime) {
          updated.month = [];
          updated.year = [];
        }

        if (name === "progressionTime" && !isProgressionMonths) {
          updated.progressionMonths = [];
        }
        if (name === "progressionTime" && !isProgressionYears) {
          updated.progressionYears = [];
        }

        if (isTradesChange) {
          updated.projectWorker = [];
          updated.projectManager = [];
        }

        return updated;
      });
      if (name === "time" || name === "Custom Selection") {
        dispatch(setDashboardFlag("dashboard-data"))
      } else if (name === "projectType") {
        dispatch(setDashboardFlag("dashboard-project-type-data"))
      } else if (name === "progressionTime") {
        dispatch(setDashboardFlag("graph-data"))
      } else if (name === "trades") {
        dispatch(setDashboardFlag("dashboard-trades-type-worker-manager"))
      } else {
        dispatch(setDashboardFlag(flagValue))
      }

      // URL update (single source)
      setMultipleParams({
        [name]: isDepartmentItem(value) ? value.name : value,
        ...(name === "time" && !isCustomTime && { month: null, year: null }),
        ...(name === "progressionTime" && !isProgressionMonths && { progressionMonths: [] }),
        ...(name === "progressionTime" && !isProgressionYears && { progressionYears: [] }),
        ...(isDepartmentItem(value) && value.name !== "All" && { departmentId: value._id }),
        ...(isDepartmentItem(value) && value.name === "All" && { departmentId: null }),
        ...(isTradesChange && { projectWorker: [], projectManager: [] }),
      });
      setIsPopoverOpen("");
      setProgressionFilterPopover("");
    },
    [setMultipleParams]
  );

  // Toggle project manager or worker selection
  const toggleManagerHandle = useCallback((name: string, id: string, flagValue: string) => {
    setFilterValue((prev: any) => {
      const arr = prev[name]
      const finalValue = arr.includes(id) ? arr.filter((item: any) => item !== id) : [...arr, id]
      setParam(name, finalValue);
      return {
        ...prev,
        [name]: finalValue,
      };
    });
    dispatch(setDashboardFlag(flagValue))
  }, [setParam, dispatch]);

  // Toggle month or year selection
  const toggleYearMonthHandle = useCallback((name: string, value: string | number, flagValue: string) => {

    const arr = filterValue[name] ?? []

    const finalValue = arr.includes(value) ? arr.filter((item: any) => item !== value) : [...arr, value]

    setParam(name, finalValue);
    setFilterValue((prev: any) => ({
      ...prev,
      [name]: finalValue,
    }
    ));
    dispatch(setDashboardFlag(flagValue))
  }, [setParam, dispatch]);

  const handleAllManagerSelection = (key: string, flagValue: string) => {
    const allManagerIds = managersData.map((manager: any) => manager._id);
    const selected = filterValue[key] ?? []
    const isAllManagersSelected = allManagerIds.every((id: string) =>
      selected.includes(id)
    );
    const newValue = isAllManagersSelected ? [] : allManagerIds;


    if (isAllManagersSelected) {
      setFilterValue((prev) => ({
        ...prev,
        [key]: [],
      }))
    } else {
      setFilterValue((prev) => ({
        ...prev,
        [key]: allManagerIds,
      }))
    }
    if (isAllManagersSelected) removeParam([key]);
    else setParam(key, newValue);

    dispatch(setDashboardFlag(flagValue))
  };

  const clearFilters = () => {
    removeAllParams();
    setFilterValue((prev) => ({
      ...prev,
      time: "YTD",
      projectType: "All Projects",
      trades: "",
      year: [],
      month: [],
      projectManager: [],
      projectWorker: [],
      progressionTime: "Historic",
      progressionMonths: [],
      progressionYears: []
    }));
    dispatch(setDashboardFlag("all-data"))
    return;
  };

  const handleAllMonthSelection = (key: string, flagValue: string, selectedMonthsList: string[], isSearchExist: boolean) => {
    setFilterValue((prev) => {
      const selectedMonths: string[] = prev[key] ?? [];
      let newValue = []
      const isAllSelected =
        selectedMonths?.length === selectedMonthsList?.length &&
        selectedMonthsList.every((m) => selectedMonths.includes(m));
      if (isSearchExist) {
        newValue = [...selectedMonths, ...selectedMonthsList]
      } else {
        newValue = isAllSelected ? [] : [...selectedMonthsList];
      }

      if (newValue?.length === 0) removeParam([key]);
      else setArrayParam(key, newValue);
      dispatch(setDashboardFlag(flagValue))

      return {
        ...prev,
        [key]: newValue,
      };
    });
  };

  const handleAllYearSelection = (key: string, flagValue: string, selectedYearsList: number[], isSearchExist: boolean) => {
    setFilterValue((prev) => {
      const selectedYears: number[] = prev[key] ?? [];
      let newValue = []
      const isAllSelected =
        selectedYears?.length === selectedYearsList?.length &&
        selectedYearsList.every((y: any) => selectedYears.includes(y));

      if (isSearchExist) {
        newValue = [...selectedYears, ...selectedYearsList]
      } else {
        newValue = isAllSelected ? [] : [...selectedYearsList];
      }

      if (newValue?.length === 0) removeParam([key]);
      else setArrayParam(key, newValue);
      dispatch(setDashboardFlag(flagValue))
      return {
        ...prev,
        [key]: newValue,
      };
    });
  };


  const isFilterChanged = (): boolean => {
    const initial: FilterValue = {
      time: "YTD",
      projectType: "All Projects",
      trades: "",
      year: [],
      month: [],
      projectManager: [],
      projectWorker: [],
      progressionTime: "YTD",
      progressionMonths: [],
      progressionYears: []
    };

    for (const key of Object.keys(initial) as (keyof FilterValue)[]) {
      const currentValue = filterValue[key];
      const initialValue = initial[key];

      if (Array.isArray(initialValue) && Array.isArray(currentValue)) {
        if (
          initialValue?.length !== currentValue?.length ||
          initialValue?.some((v, i) => v !== currentValue[i])
        ) {
          return true;
        }
      } else if (currentValue !== initialValue) {
        return true;
      }
    }

    return false;
  };

  return (
    <div className="main-content">
      <Box sx={{ minHeight: "100vh", bg: bgDashboard, fontFamily: "poppins" }}>
        <Box>
          <Box
            sx={{
              width: "100%",
              background: bgDashboard,
              p: "20px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: ["flex-start", "flex-start", "flex-start", "center"],
              flexDirection: ["column", "column", "column", "row"],
            }}
          >
            <Box sx={{ display: 'flex', gap: "10px" }}>
              <Heading as="h2" sx={{ fontSize: [4, 5], color: titleHeader }}>
                Dashboard
              </Heading>
              {isFilterChanged() && <ClearFilterButton onClick={() => (!allDataLoading && !progressionLoading) && clearFilters()} label={"Clear Filters"} sx={{ minWidth: '113px' }} />}
            </Box>

            <DashboardFilters
              value={filterValue}
              popoverRef={popoverRef}
              isPopoverOpen={isPopoverOpen}
              togglePopover={togglePopover}
              handleOptionClick={handleOptionClick}
              showMonthYearPicker={showMonthYearPicker}
              months={months}
              showProjectManagerList={showProjectManagerList}
              projectManagerList={managersData}
              toggleManagerHandle={toggleManagerHandle}
              toggleYearMonthHandle={toggleYearMonthHandle}
              handleAllManagerSelection={handleAllManagerSelection}
              handleAllMonthSelection={handleAllMonthSelection}
              handleAllYearSelection={handleAllYearSelection}
              loading={allDataLoading}
            />
          </Box>
          <Container sx={{ padding: "20px", backgroundColor: colorMode === 'light' ? "#FFFBF5" : "#20283c", display: "flex", flexDirection: "column", gap: "20px" }}>
            <ProjectOverview projectOverview={dashboardData?.projectOverview} loading={allDataLoading} />
            <ResidentialAndCommercial approvedProject={dashboardData?.approvedProject} projectInsights={dashboardData?.projectInsights} tradesDistribution={dashboardData?.tradesDistribution} mapData={dashboardData?.mapData} loading={allDataLoading} />
            <Grid columns={[1, 1, 1, '400px 1fr']} gap="20px">
              <Box sx={{ gap: '24px' }}>
                <TradesDistribution tradesDistribution={dashboardData?.tradesDistribution} loading={allDataLoading} />
              </Box>

              <Box sx={{ gap: '40px' }}>
                <DoneProjectBreakdown doneProjectBreakdown={dashboardData?.doneProjectBreakdown} loading={allDataLoading} />
              </Box>
            </Grid>
            <ProjectProgressChart
              popoverRef={progressionFilterRef}
              months={months}
              graphData={graphData}
              loading={progressionLoading}
              progressionChartFilterValue={filterValue}
              isPopoverOpen={progressionFilterPopover}
              handleProjectProgressionFilterClick={handleOptionClick}
              togglePopover={togglePopover}
              toggleYearMonthHandle={toggleYearMonthHandle}
              showMonthYearPicker={showMonthsPicker}
              handleAllMonthSelection={handleAllMonthSelection}
              handleAllYearSelection={handleAllYearSelection} />
          </Container >
        </Box >
      </Box >
    </div >
  );
};


export default DashboardComponent;